// import React from 'react';
import { ActionCreator } from 'redux';
import { Template, TemplateType } from '@dev/ee-api/ee-api-models';
import { isEmpty, remove, replace, find } from 'lodash-es';
import { store } from '@shared-store/shared-store';

import {
    FetchExistingTemplateStartAction,
    FetchExistingTemplateSuccessAction,
    FetchExistingTemplateErrorAction,
    AddTemplateTagsAction,
    RemoveTemplateTagsAction,
    ModifyTextBodyAction,
    ModifyAMPBodyAction,
    CloseEditorSuccessAction,
    SaveChangesAfterCloseEditorSuccessAction,
    ChangeDefaultOptionsAction,
    ChangeDefaultOptionsActionParams,
    ChangeFromAction,
    ChangeSubjectAction,
    SendTestEmailAction,
    LoadTemplateAction,
    SaveTemplateErrorAction,
    SaveTemplateStartAction,
    SaveTemplateSuccessAction,
    RedoTemplateChangesAction,
    UndoTemplateChangesAction,
    ModifyTemplateNameAction,
    ModifyTemplateNameSuccessAction,
    CloseEditorAction,
    ChangeToAction,
    ChangeFromNameAction,
    ChangeTemplateBodyAction,
    SavePastBodyAction,
    SaveLastScreenshotAction,
    GetDefaultTemplateStartAction,
    GetDefaultTemplateSuccessAction,
    GetDefaultTemplateErrorAction,
    RawEditorActionTypes,
    SaveScreenshotAction,
    ChangePreviewTextAction,
} from './types';
import defaultTemplate from '../../components/Editor/templates/defaultHtmlTemplate.html';
import { isReseller } from '../../services/Security';

declare const ee: any;
declare const window: any;

const replaceResellerLinks = (body: string): string => {
    const getApiPath = (): string => {
        if (!store.getState().config.isStandalone && window.ee) {
            return ee.api.path;
        }

        return APIPATH;
    };
    //TODO: remove this after migration to multiproduct
    const apiPath = getApiPath();

    return replace(body, new RegExp('https://api.elasticemail.com', 'g'), apiPath);
};
const createTemplateObject = (name: string, body = defaultTemplate, subject = '', fromName = ''): Template => {
    const account = store.getState().account;
    const validEmails = store.getState().validEmails;
    const validEmail = find(validEmails?.emails, 'Validated');
    if (isReseller()) {
        body = replaceResellerLinks(body);
    }
    const firstName = account.Profile.FirstName ? `${account.Profile.FirstName} ` : '';
    const lastName = account.Profile.LastName ?? '';
    return {
        Name: name,
        FromName: fromName || account.Profile.FirstName || account.Profile.LastName ? `${firstName}${lastName}` : '',
        FromEmail: validEmail?.Email || account.Profile.Email,
        Body: {
            Subject: subject || name,
            Parts: [
                {
                    ContentType: 'text/html',
                    Content: body,
                },
            ],
        },
    };
};
export const getTemplateObject = (template: string): Template => {
    const rawEditorState = store.getState().rawEditor;
    const { templateObject } = rawEditorState;
    const updatedMimeObj = { ContentType: 'text/html', Content: template };
    if (!templateObject) {
        return createTemplateObject(rawEditorState.templateName, template, rawEditorState.templateName);
    }
    const { Parts } = templateObject.Body;
    if (isEmpty(Parts)) {
        templateObject.Body.Parts = [updatedMimeObj];
    } else {
        templateObject.Body.Parts = remove(Parts, (o) => o === 'text/html');
        templateObject.Body.Parts.push(updatedMimeObj);
    }
    return templateObject;
};
export const AddTag: ActionCreator<AddTemplateTagsAction> = (
    tag: string,
    previewRef: React.Ref<HTMLIFrameElement>
): AddTemplateTagsAction => {
    return {
        type: RawEditorActionTypes.ADD_TAG,
        payload: {
            tag,
            previewRef,
        },
    };
};
export const RemoveTag: ActionCreator<RemoveTemplateTagsAction> = (
    tag: string,
    previewRef: React.Ref<HTMLIFrameElement>
): RemoveTemplateTagsAction => {
    return {
        type: RawEditorActionTypes.REMOVE_TAG,
        payload: {
            tag,
            previewRef,
        },
    };
};

export const ChangeName: ActionCreator<ModifyTemplateNameAction> = (
    name: string,
    previewRef: React.Ref<HTMLIFrameElement>
): ModifyTemplateNameAction => {
    return {
        type: RawEditorActionTypes.CHANGE_TEMPLATE_NAME,
        payload: {
            name,
            previewRef,
        },
    };
};

export const ChangeNameSuccess: ActionCreator<ModifyTemplateNameSuccessAction> = (name: string): ModifyTemplateNameSuccessAction => {
    return {
        type: RawEditorActionTypes.CHANGE_TEMPLATE_NAME_SUCCESS,
        payload: {
            name,
        },
    };
};

export const GetDefaultTemplateStart: ActionCreator<GetDefaultTemplateStartAction> = (
    templateType: TemplateType
): GetDefaultTemplateStartAction => {
    return {
        type: RawEditorActionTypes.GET_DEFAULT_TEMPLATE_START,
        payload: {
            templateType,
        },
    };
};

export const GetDefaultTemplateSuccess: ActionCreator<GetDefaultTemplateSuccessAction> = (
    template: Template
): GetDefaultTemplateSuccessAction => {
    return {
        type: RawEditorActionTypes.GET_DEFAULT_TEMPLATE_SUCCESS,
        payload: {
            template,
            isNewTemplate: true,
        },
    };
};

export const GetDefaultTemplateError: ActionCreator<GetDefaultTemplateErrorAction> = (): GetDefaultTemplateErrorAction => {
    return {
        type: RawEditorActionTypes.GET_DEFAULT_TEMPLATE_ERROR,
        payload: {
            isLoading: false,
        },
    };
};

export const FetchTemplateSuccess: ActionCreator<FetchExistingTemplateSuccessAction> = (
    template: Template,
    templateName: string,
    isNewTemplate: boolean
): FetchExistingTemplateSuccessAction => {
    let body = defaultTemplate;
    if (isReseller()) {
        body = replaceResellerLinks(body);
    }
    if (template) {
        const bodyPart = template.Body?.Parts?.find((o) => o.ContentType === 'text/html') ?? null;
        if (bodyPart) {
            body = isReseller() ? replaceResellerLinks(bodyPart.Content) : bodyPart.Content;
        } else {
            body = '';
        }
    }
    return {
        type: RawEditorActionTypes.FETCH_SUCCESS,
        payload: {
            isNewTemplate,
            currentBody: body,
            templateObject: template || createTemplateObject(templateName),
        },
    };
};
export const FetchTemplateError: ActionCreator<FetchExistingTemplateErrorAction> = (
    template = defaultTemplate,
    templateName: string
): FetchExistingTemplateErrorAction => {
    if (isReseller()) {
        template = replaceResellerLinks(template);
    }
    return {
        type: RawEditorActionTypes.FETCH_ERROR,
        payload: {
            templateLoading: false,
            currentBody: template,
            templateObject: createTemplateObject(templateName),
        },
    };
};

export const FetchTemplate: ActionCreator<FetchExistingTemplateStartAction> = (): FetchExistingTemplateStartAction => {
    return {
        type: RawEditorActionTypes.FETCH_START,
    };
};

export const LoadTemplate: ActionCreator<LoadTemplateAction> = (name: string, isNew: boolean, isPublic?: boolean): LoadTemplateAction => {
    return {
        type: RawEditorActionTypes.LOAD_TEMPLATE,
        payload: {
            templateName: name,
            isNewTemplate: isNew,
            isPublic,
        },
    };
};

export const ChangeTemplateBody: ActionCreator<ChangeTemplateBodyAction> = (template = '<html></html>'): ChangeTemplateBodyAction => {
    return {
        type: RawEditorActionTypes.CHANGE_TEMPLATE_BODY,
        payload: {
            currentBody: template,
        },
    };
};

export const SavePastBody: ActionCreator<SavePastBodyAction> = (
    template?: string,
    previewRef?: React.Ref<HTMLIFrameElement>
): SavePastBodyAction => {
    return {
        type: RawEditorActionTypes.SAVE_PAST_BODY,
        payload: {
            pastBody: template,
            previewRef,
        },
    };
};

export const SaveChanges: ActionCreator<SaveTemplateStartAction> = (): SaveTemplateStartAction => {
    return {
        type: RawEditorActionTypes.SAVE_CHANGES_START,
    };
};

export const SaveChangesSuccess: ActionCreator<SaveTemplateSuccessAction> = (): SaveTemplateSuccessAction => {
    return {
        type: RawEditorActionTypes.SAVE_CHANGES_SUCCESS,
    };
};

export const SaveChangesError: ActionCreator<SaveTemplateErrorAction> = (): SaveTemplateErrorAction => {
    return {
        type: RawEditorActionTypes.SAVE_CHANGES_ERROR,
    };
};

export const RedoTemplateChanges: ActionCreator<RedoTemplateChangesAction> = (
    previewRef: React.Ref<HTMLIFrameElement>
): RedoTemplateChangesAction => {
    return {
        type: RawEditorActionTypes.REDO_CHANGES,
        payload: {
            previewRef,
        },
    };
};

export const UndoTemplateChanges: ActionCreator<UndoTemplateChangesAction> = (
    previewRef: React.Ref<HTMLIFrameElement>
): UndoTemplateChangesAction => {
    return {
        type: RawEditorActionTypes.UNDO_CHANGES,
        payload: {
            previewRef,
        },
    };
};

export const CloseTemplateEditor: ActionCreator<CloseEditorAction> = (previewRef: React.Ref<HTMLIFrameElement>): CloseEditorAction => {
    return {
        type: RawEditorActionTypes.CLOSE_EDITOR,
        payload: {
            previewRef,
        },
    };
};

export const ChangeToEmail: ActionCreator<ChangeToAction> = (sendTo: string): ChangeToAction => {
    return {
        type: RawEditorActionTypes.MODIFY_TO,
        payload: {
            sendTo,
        },
    };
};
export const CloseTemplateEditorSuccess: ActionCreator<CloseEditorSuccessAction> = (): CloseEditorSuccessAction => {
    return {
        type: RawEditorActionTypes.CLOSE_EDITOR_SUCCESS,
    };
};
export const SaveChangesAfterCloseEditorSuccess: ActionCreator<
    SaveChangesAfterCloseEditorSuccessAction
> = (): SaveChangesAfterCloseEditorSuccessAction => {
    return {
        type: RawEditorActionTypes.SAVE_CHANGES_AFTER_CLOSE_EDITOR_SUCCESS,
    };
};
export const ModifyTextBody: ActionCreator<ModifyTextBodyAction> = (
    textBody: string,
    previewRef: React.Ref<HTMLIFrameElement>
): ModifyTextBodyAction => {
    return {
        type: RawEditorActionTypes.MODIFY_TEXT_BODY,
        payload: {
            textBody,
            previewRef,
        },
    };
};
export const ModifyAMPBody: ActionCreator<ModifyAMPBodyAction> = (
    ampBody: string,
    previewRef: React.Ref<HTMLIFrameElement>
): ModifyAMPBodyAction => {
    return {
        type: RawEditorActionTypes.MODIFY_AMP_BODY,
        payload: {
            ampBody,
            previewRef,
        },
    };
};
export const ChangeSubject: ActionCreator<ChangeSubjectAction> = (
    subject: string,
    previewRef: React.Ref<HTMLIFrameElement>
): ChangeSubjectAction => {
    return {
        type: RawEditorActionTypes.MODIFY_SUBJECT,
        payload: {
            subject,
            previewRef,
        },
    };
};

export const ChangePreviewText = (previewText: string): ChangePreviewTextAction => ({
    type: RawEditorActionTypes.MODIFY_PREVIEW_TEXT,
    payload: {
        previewText,
    },
});
export const ChangeFromEmail: ActionCreator<ChangeFromAction> = (
    fromEmail: string,
    previewRef: React.Ref<HTMLIFrameElement>
): ChangeFromAction => {
    return {
        type: RawEditorActionTypes.MODIFY_FROM,
        payload: {
            fromEmail,
            previewRef,
        },
    };
};
export const ChangeFromName: ActionCreator<ChangeFromNameAction> = (
    fromName: string,
    previewRef: React.Ref<HTMLIFrameElement>
): ChangeFromNameAction => {
    return {
        type: RawEditorActionTypes.MODIFY_FROM_NAME,
        payload: {
            fromName,
            previewRef,
        },
    };
};
export const SendTestEmail: ActionCreator<SendTestEmailAction> = (): SendTestEmailAction => {
    return {
        type: RawEditorActionTypes.SEND_TEST_EMAIL,
    };
};

export const SaveLastScreenshot: ActionCreator<SaveLastScreenshotAction> = (screenshot: string): SaveLastScreenshotAction => {
    return {
        type: RawEditorActionTypes.SAVE_LAST_SCREENSHOT,
        payload: {
            lastScreenshot: screenshot,
        },
    };
};
export const ChangeDefaultOptions: ActionCreator<ChangeDefaultOptionsAction> = (
    params: ChangeDefaultOptionsActionParams
): ChangeDefaultOptionsAction => {
    return {
        type: RawEditorActionTypes.CHANGE_DEFAULT_OPTIONS,
        payload: {
            params,
            previewRef: params.previewRef,
        },
    };
};

export const SaveScreenshot = (previewRef: React.Ref<HTMLIFrameElement>): SaveScreenshotAction => ({
    type: RawEditorActionTypes.SAVE_SCREENSHOT,
    payload: {
        previewRef,
    },
});
